import { checkEmployeeHierarchy } from "@/api/utils";
import { OpenFolderButton } from "@/components/Buttons";
import { ProjectProps } from "@/interfaces/project_types";
import { useState, useMemo } from "react";
import { SubmittalOverview } from "./UI/SubmittalOverview";
import { TaskOverview } from "./UI/TaskOverview";
import { Description } from "./UI/Description";
import { calculateCategoryCounts } from "./Utils/calculateCategoryCounts";
import { useAuth } from "@/App";
import { SimilarProjects } from "./UI/SimilarProjects";
import { DownloadDocumentsButton, DownloadProposalsButton } from "./UI/DownloadDocumentsButton";
import { CVDocumentsButton, CreateRFIButton, CVProposalsButton, UploadDocumentButton, UploadProposalButton } from "@/components/_Shared/Buttons/ProjectRouteButtons";
import { CreateSubmittalButton } from "@/components/_Shared/Buttons/ProjectRouteButtons";
import { AssignTasksButton } from "@/components/_Shared/Buttons/ProjectRouteButtons";
import { UpdateProjectButton } from "@/components/_Shared/Buttons/ProjectRouteButtons";
import { GenerateQRButton } from "./UI/GenerateQR";
import { RoleHierarchy } from "@/interfaces/employee_type";

export type CategoryCounts = {
    Mechanical: { total: number; completed: number; };
    Electrical: { total: number; completed: number; };
    Plumbing: { total: number; completed: number; };
    "Fire Protection": { total: number; completed: number; };
    Other: { total: number; completed: number; };
}

/**
 * A Component that shows when the user clicks on a row on the table.
 * This is where the description of the project will be stored
 * 
 * TODO: Refactor this so that all the calculations are done in the backend
 *
 */
export const ProjectRow = ({ data }: { data: ProjectProps }) => {
    const { user } = useAuth()
    const role = user?.role ?? ""
    
    // I might refactor this into a hook later
    // Or toss them into their own respective components
    const submittals = useMemo(() => data.submittals ?? [], [data.submittals])
    const tasks = useMemo(() => data.tasks ?? [], [data.tasks])

    const [submittalCounts, setSubmittalCounts] = useState<CategoryCounts>({
        Mechanical: { total: 0, completed: 0 },
        Electrical: { total: 0, completed: 0 },
        Plumbing: { total: 0, completed: 0 },
        "Fire Protection": { total: 0, completed: 0 },
        Other: { total: 0, completed: 0 }
    })

    const [taskCounts, setTaskCounts] = useState<CategoryCounts>({
        Mechanical: { total: 0, completed: 0 },
        Electrical: { total: 0, completed: 0 },
        Plumbing: { total: 0, completed: 0 },
        "Fire Protection": { total: 0, completed: 0 },
        Other: { total: 0, completed: 0 }
    })

    useMemo(() => {
        setTaskCounts(calculateCategoryCounts(tasks))
        setSubmittalCounts(calculateCategoryCounts(submittals))
    }, [tasks, submittals]);

    const totalTasks = Object.values(taskCounts).reduce((sum, { total }) => sum + total, 0);
    const totalCompletedTasks = Object.values(taskCounts).reduce((sum, { completed }) => sum + completed, 0);  
    const taskTotalProgress = totalTasks > 0 ? (totalCompletedTasks / totalTasks) * 100 : 0;

    const role_hierarchy = checkEmployeeHierarchy(role)

    /*
    const handleDelete = async () => {
        if (confirm("Are you sure you want to delete this project?") && confirm("Are you really sure?")) {
            try {
                if (!data.project_id) {
                    throw new Error("Project ID is undefined")
                }

                const response = await deleteProject(data.project_id)
                if (response === 204) {
                    alert("Project deleted successfully")
                    window.location.href = "/projects/"
                } else {
                    alert("Error deleting project:" + response)
                }
            } catch (error: unknown) {
                if (error instanceof AxiosError) {
                    if (error.response?.status === 400) {
                        // 400 Bad Request
                        return 400
                    } else if (error.response?.status === 403) {
                        // 403 Forbidden
                        return 403
                    }
                }
                alert("Error deleting project: Error" + error)
            }
        }
    }
    */

    const totalSubmittals = Object.values(submittalCounts).reduce((sum, { total }) => sum + total, 0);
    const totalCompletedSubmittals = Object.values(submittalCounts).reduce((sum, { completed }) => sum + completed, 0);
    const submittalTotalProgress = totalSubmittals > 0 ? (totalCompletedSubmittals / totalSubmittals) * 100 : 0;
    
    return (
        <div className="flex flex-col w-full bg-stone-50 dark:bg-stone-900">
            <div className="flex flex-row gap-5 m-5">
                {ButtonsFormatFactory(role_hierarchy, data.project_id ?? "", data.folder_location ?? "")}

            </div>

           {/* CHANGE THIS TO MAKE IT EASIER TO FORMAT */}
            {( role_hierarchy === "ADMIN" || role_hierarchy === "MANAGERS" || role_hierarchy === "IT" || role_hierarchy === "CEO" ) &&
            <>
            <div className="flex flex-col gap-5 p-5">
                <Description description={data.description ?? ""} />

                <TaskOverview tasks={tasks} taskCounts={taskCounts} totalTasks={totalTasks} totalProgress={taskTotalProgress} />

                <SubmittalOverview submittals={submittals} submittalCounts={submittalCounts} totalSubmittals={totalSubmittals} totalProgress={submittalTotalProgress} />
            </div>

            <div className="flex flex-col gap-5 p-5">
                <SimilarProjects project={data} />
            </div>

            </>}
        </div>
    );
};

// Simple factory that just returns the correct button format based on the user's role
function ButtonsFormatFactory(role: keyof typeof RoleHierarchy, project_id: string, folder_location: string) {
    switch (role) {
        case "ADMIN":
        case "CEO":
        case "MANAGERS":
        case "IT":
            return <AllButtons project_id={project_id} folder_location={folder_location} />
        case "FINANCE":
            return <FinanceButtons project_id={project_id} folder_location={folder_location} />
        case "TEAM":
            return <TeamMemberButtons project_id={project_id} folder_location={folder_location} />
        default:
            return <></>
    }
}

function AllButtons({ project_id, folder_location }: { project_id: string, folder_location: string }) {
    return (
        <>
            <UpdateProjectButton project_id={project_id} />
            <AssignTasksButton project_id={project_id} />
            <CreateSubmittalButton project_id={project_id} />
            <CreateRFIButton project_id={project_id} />
            <UploadDocumentButton project_id={project_id} folder_location={folder_location} />
            <UploadProposalButton project_id={project_id} folder_location={folder_location} />
            <CVDocumentsButton project_id={project_id} />
            <CVProposalsButton project_id={project_id} />
            <DownloadDocumentsButton project_id={project_id} />
            <GenerateQRButton project_id={project_id} />
            {/* <OpenFolderButton folder_path={'projects\\' + data.folder_location} /> For the cloud*/}
            <OpenFolderButton folder_path={folder_location ?? ""} />
        </>
    )
}

function FinanceButtons({ project_id, folder_location }: { project_id: string, folder_location: string }) {
    return (
        <>
            <UploadProposalButton project_id={project_id} folder_location={folder_location} />
            <CVProposalsButton project_id={project_id} />
            <DownloadDocumentsButton project_id={project_id} />
            <DownloadProposalsButton project_id={project_id} />
        </>
    )
}

function TeamMemberButtons({ project_id, folder_location }: { project_id: string, folder_location: string }) {
    return (
        <>
            <UploadDocumentButton project_id={project_id} folder_location={folder_location} />
            <GenerateQRButton project_id={project_id} />
        </>
    )
}